/**
 * uOqAt 设置 hub 页 — frame uOqAt @ ui/dogdisk-user.pen 页面-设置
 *
 * 入口:QQKOK 我的页 "账户设置" row (m4bGp) → /profile/settings
 * 子页:
 *  - menuItem 通知 → /profile/notifications (Y0dLm)
 *  - menuItem 语言 → /profile/language (O3Zta)
 *  - menuItem 修改密码 → /profile/change-password (U4SJh)
 *  - menuItem 两步验证 → /profile/mfa (qcBV7)
 *  - menuItem 关于(协议/隐私/关于) → /about
 *
 * 4 Section:
 *  - 通用(general):itemPublicProfile toggle / itemSearchable disabled
 *  - 隐私(privacy):itemSearchable(disabled MVP "敬请期待")
 *  - 账户安全(security):4 menuItem(notifications/language/change-password/mfa)
 *  - 关于(about):itemVersion + menuItem 关于
 *
 * Spec:docs/superpowers/specs/2026-05-26-batch-d-user-routes-design.md §4.1
 * Backend:0 新(PATCH profiles.public_profile_enabled 复用 PostgREST RLS)
 */
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Spinner, getSupabase, useCurrentUser, cn } from '@dogdisk/shared'
import { AppLayout } from '../../components/app-layout'
import { MobileScreen, TopBar } from '../../components/mobile-screen'

const F = 'uOqAt'

/**
 * 应用版本 — build-time 由 vite `define` 注入 `import.meta.env.VITE_APP_VERSION`;
 * 未注入时(本地 dev / 测试栈)fallback `0.0.0-dev`。
 */
const APP_VERSION =
  (typeof import.meta !== 'undefined' &&
    (import.meta.env as Record<string, string | undefined>)?.['VITE_APP_VERSION']) ||
  '0.0.0-dev'

type ProfileSettings = {
  public_profile_enabled: boolean
}

export function SettingsPage() {
  const navigate = useNavigate()
  const { user } = useCurrentUser()
  const qc = useQueryClient()
  const [publicEnabled, setPublicEnabled] = useState(true)

  const query = useQuery<ProfileSettings | null>({
    queryKey: ['user', 'me', 'profile', 'settings', user?.user_id] as const,
    enabled: Boolean(user),
    queryFn: async () => {
      if (!user) return null
      const sb = getSupabase()
      const { data, error } = await sb
        .from('v_profile_self')
        .select('public_profile_enabled')
        .single()
      if (error) throw error
      return (data as ProfileSettings) ?? null
    },
  })

  useEffect(() => {
    if (query.data) setPublicEnabled(Boolean(query.data.public_profile_enabled))
  }, [query.data])

  const togglePublic = useMutation({
    mutationFn: async (next: boolean) => {
      // 用 supabase getUser() 取当前用户,避免依赖 useCurrentUser hook 的 react-query
      // 时序(spec/手测发现 click 触发时 useCurrentUser().user 可能仍 undefined,
      // 因为 useCurrentUser 内部 useQuery 异步且本页 ToggleRow 不 gate user)
      const sb = getSupabase()
      const { data: au } = await sb.auth.getUser()
      const uid = au.user?.id
      if (!uid) throw new Error('未登录')
      const { error } = await sb
        .from('profiles')
        .update({ public_profile_enabled: next })
        .eq('user_id', uid)
      if (error) throw error
      return next
    },
    onSuccess: (next) => {
      setPublicEnabled(next)
      void qc.invalidateQueries({ queryKey: ['user', 'me', 'profile', 'settings'] })
    },
  })

  if (query.isLoading) {
    return (
      <AppLayout>
        <MobileScreen>
          <TopBar title="设置" />
          <div className="flex flex-1 items-center justify-center py-12">
            <Spinner className="text-gray-400" />
          </div>
        </MobileScreen>
      </AppLayout>
    )
  }

  return (
    <AppLayout>
      <MobileScreen bg="surface">
        <div data-testid={F}>
          <TopBar title="设置" />

          {/* Section 1: 通用 */}
          <section data-testid={`${F}-section-general`} className="mt-3 bg-white">
            <SectionHeader>通用</SectionHeader>
            <ToggleRow
              testId={`${F}-tog-publicProfile`}
              label="公开个人主页"
              hint="关闭后,他人通过 /u/{你} 无法访问"
              checked={publicEnabled}
              disabled={togglePublic.isPending}
              onChange={(v) => togglePublic.mutate(v)}
            />
          </section>

          {/* Section 2: 隐私 */}
          <section data-testid={`${F}-section-privacy`} className="mt-3 bg-white">
            <SectionHeader>隐私</SectionHeader>
            <ToggleRow
              testId={`${F}-tog-searchable`}
              label="允许搜索到我"
              checked={false}
              disabled
              hint={
                <span data-testid={`${F}-tog-searchable-hint`} className="text-ink-mute">
                  敬请期待
                </span>
              }
              onChange={() => undefined}
            />
          </section>

          {/* Section 3: 账户安全 */}
          <section data-testid={`${F}-section-security`} className="mt-3 bg-white">
            <SectionHeader>账户安全</SectionHeader>
            <MenuRow
              testId={`${F}-mi-notifications`}
              label="通知设置"
              onClick={() => navigate('/profile/notifications')}
            />
            <MenuRow
              testId={`${F}-mi-language`}
              label="语言设置"
              onClick={() => navigate('/profile/language')}
            />
            <MenuRow
              testId={`${F}-mi-change-password`}
              label="修改密码"
              onClick={() => navigate('/profile/change-password')}
            />
            <MenuRow
              testId={`${F}-mi-mfa`}
              label="两步验证"
              onClick={() => navigate('/profile/mfa')}
            />
          </section>

          {/* Section 4: 关于 */}
          <section data-testid={`${F}-section-about`} className="mt-3 mb-8 bg-white">
            <SectionHeader>关于</SectionHeader>
            <MenuRow
              testId={`${F}-mi-about`}
              label="关于 绅士网盘"
              onClick={() => navigate('/about')}
            />
            <div
              className="flex items-center justify-between px-4 py-3 text-[14px] text-ink-mute"
              data-testid={`${F}-version-row`}
            >
              <span>版本</span>
              <span data-testid={`${F}-version`}>{APP_VERSION}</span>
            </div>
          </section>
        </div>
      </MobileScreen>
    </AppLayout>
  )
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="px-4 pt-3 pb-1 text-[12px] font-medium text-ink-mute">{children}</h2>
  )
}

function ToggleRow({
  testId,
  label,
  hint,
  checked,
  disabled,
  onChange,
}: {
  testId: string
  label: string
  hint?: React.ReactNode
  checked: boolean
  disabled?: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border-subtle last:border-0">
      <div className="flex flex-col gap-0.5">
        <span className="text-[15px] text-ink">{label}</span>
        {hint && <span className="text-[12px] text-ink-mute">{hint}</span>}
      </div>
      <button
        type="button"
        role="switch"
        data-testid={testId}
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors',
          checked ? 'bg-brand-500' : 'bg-border-strong',
          disabled ? 'opacity-50' : '',
        )}
      >
        <span
          className={cn(
            'inline-block h-5 w-5 rounded-full bg-white shadow transition-transform',
            checked ? 'translate-x-[22px]' : 'translate-x-0.5',
          )}
        />
      </button>
    </div>
  )
}

function MenuRow({
  testId,
  label,
  onClick,
}: {
  testId: string
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-[15px] text-ink border-b border-border-subtle last:border-0 active:bg-surface-100"
    >
      <span>{label}</span>
      <span aria-hidden className="text-ink-mute">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 18l6-6-6-6" />
        </svg>
      </span>
    </button>
  )
}

export default SettingsPage
